import { coerceArray } from '@angular/cdk/coercion';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isset, translateDictionary } from '@cawita/core-front';
import { AlertButton, AlertController, AlertInput, IonicSafeString } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { normalizeTranslatableString, TranslatableString } from '@shared/models';

export interface ChoiceAlertButton {
    label: TranslatableString;
    role: string;
    style?: 'primary' | 'danger' | 'secondary';
}

export interface SelectAlertOption<R> {
    label: TranslatableString;
    value: R;
}

export interface ConfirmationAlertConfig {
    title: TranslatableString;
    subtitle?: TranslatableString;
    message: TranslatableString | TranslatableString[];
    confirmText?: TranslatableString;
    cancelText?: TranslatableString;
    danger?: boolean;
}

export interface ShowAlertConfig {
    title: TranslatableString;
    message: TranslatableString | TranslatableString[];
    closeText?: TranslatableString;
    danger?: boolean;
}

export interface ChoiceAlertConfig {
    title: TranslatableString;
    subtitle?: TranslatableString;
    message: TranslatableString | TranslatableString[];
    buttons: ChoiceAlertButton[];
}

export interface SelectAlertConfig<R> {
    title: TranslatableString;
    subtitle?: TranslatableString;
    message: TranslatableString | TranslatableString[];
    confirmText?: TranslatableString;
    cancelText?: TranslatableString;
    options: SelectAlertOption<R>[];
}

export interface InputAlertConfig {
    title: TranslatableString;
    subtitle?: TranslatableString;
    placeholder?: TranslatableString;
    type?: AlertInput['type'];
    message: TranslatableString | TranslatableString[];
    confirmText?: TranslatableString;
    cancelText?: TranslatableString;
}

@Injectable({ providedIn: 'root' })
export class AlertService {
    constructor(
        private alert: AlertController,
        private translate: TranslateService
    ) { }

    private translateString(str: TranslatableString | TranslatableString[]): Promise<string> {
        const toTranslate = coerceArray(str).filter(isset).map(normalizeTranslatableString);
        return Promise.all(toTranslate.map(t => this.translate.get(t.key, t.params).toPromise())).then(strings => strings.join('\n'));
    }

    public async choose<R = string>(options: ChoiceAlertConfig): Promise<R> {
        const titleI18N = await this.translateString(options.title);
        const subtitleI18N = await this.translateString(options.subtitle);
        const messageI18N = await this.translateString(options.message);
        const buttons: AlertButton[] = [];
        for (const button of options.buttons) {
            const buttonI18N = await this.translateString(button.label);
            buttons.push({
                role: button.role,
                text: buttonI18N,
                cssClass: `alert-button-${button.style ?? 'default'}`
            })
        }

        const alert = await this.alert.create({
            header: titleI18N,
            subHeader: subtitleI18N,
            message: new IonicSafeString(messageI18N),
            cssClass: `cwt-alert`,
            animated: true,
            backdropDismiss: false,
            keyboardClose: false,
            buttons: buttons
        });

        alert.present();
        const results = await alert.onWillDismiss();
        return results.role as unknown as R;
    }

    public async input(options: InputAlertConfig): Promise<string> {
        const titleI18N = await this.translateString(options.title);
        const subtitleI18N = await this.translateString(options.subtitle);
        const messageI18N = await this.translateString(options.message);
        const confirmI18N = await this.translateString(options.confirmText ?? 'action.confirm');
        const cancelI18N = await this.translateString(options.cancelText ?? 'action.cancel');
        const inputs: AlertInput[] = [{
            placeholder: await this.translateString(options.placeholder),
            type: options?.type,
            id: 'value',
            name: 'value',
        }];

        const alert = await this.alert.create({
            header: titleI18N,
            subHeader: subtitleI18N,
            message: new IonicSafeString(messageI18N),
            cssClass: `cwt-alert`,
            animated: true,
            backdropDismiss: false,
            keyboardClose: false,
            inputs: inputs,
            buttons: [
                { role: 'cancel', text: cancelI18N, cssClass: `alert-button-default` },
                { role: 'confirm', text: confirmI18N, cssClass: `alert-button-primary` }
            ]
        });

        await alert.present();
        const res = await alert.onWillDismiss();
        if (res.role !== 'confirm') return null;
        const value = res?.data?.values?.value;
        if (!value?.length) return null;
        return value;
    }

    public async select<R = string>(current: R, options: SelectAlertConfig<R>): Promise<R> {
        const titleI18N = await this.translateString(options.title);
        const subtitleI18N = await this.translateString(options.subtitle);
        const messageI18N = await this.translateString(options.message);
        const confirmI18N = await this.translateString(options.confirmText ?? 'action.confirm');
        const cancelI18N = await this.translateString(options.cancelText ?? 'action.cancel');
        const inputs: AlertInput[] = [];
        for (const option of options.options) {
            const optionsI18N = await this.translateString(option.label);
            inputs.push({
                label: optionsI18N,
                value: option.value,
                checked: current === option.value,
                type: 'radio'
            });
        }

        const alert = await this.alert.create({
            header: titleI18N,
            subHeader: subtitleI18N,
            message: new IonicSafeString(messageI18N),
            cssClass: `cwt-alert`,
            animated: true,
            backdropDismiss: false,
            keyboardClose: false,
            inputs: inputs,
            buttons: [
                { role: 'cancel', text: cancelI18N, cssClass: `alert-button-default` },
                { role: 'confirm', text: confirmI18N, cssClass: `alert-button-primary` }
            ]
        });

        await alert.present();
        const res = await alert.onWillDismiss();
        if (res.role !== 'confirm') return null;
        return res.data?.values;
    }

    public async confirm(options: ConfirmationAlertConfig): Promise<boolean> {
        const choice = await this.choose({
            title: options.title,
            subtitle: options.subtitle,
            message: options.message,
            buttons: [
                { label: options.cancelText ?? 'action.cancel', role: 'cancel' },
                { label: options.confirmText ?? 'action.confirm', role: 'confirm', style: options?.danger ? 'danger' : 'primary' },
            ]
        });

        if (choice === 'confirm') return true;
        return false;
    }

    public async confirmSimple(
        title: TranslatableString,
        message: TranslatableString,
        danger: boolean = false
    ): Promise<boolean> {
        return this.confirm({
            title: title,
            message: message,
            danger: danger
        });
    }

    public async show(options: ShowAlertConfig): Promise<void> {
        await this.choose({
            title: options.title,
            message: options.message,
            buttons: [
                { label: options.closeText ?? 'action.ok', role: 'confirm', style: options.danger ? 'danger' : 'primary' },
            ]
        });
    }


    public async showSimple(
        title: TranslatableString,
        message: TranslatableString,
        danger: boolean = false
    ): Promise<void> {
        return this.show({
            title: title,
            message: message,
            danger: danger
        });
    }

    public async httpError(httpError: HttpErrorResponse) {
        const lang = this.translate?.currentLang || this.translate?.defaultLang;
        const message = translateDictionary(httpError?.error?.translations, lang, 'api-error.default');
        return this.showSimple('api-error.title', message, false);
    }
}